/**
 * The completion toast — §05.8, as amended 2026-08-16.
 *
 * **A toast fires only when the job needs the operator to do something.**
 *
 * | transition | toast |
 * |---|---|
 * | `SUCCEEDED` | none — the drawer is persistent and already green; nothing is asked |
 * | `PARTIAL`   | **one**, linking to the job's orders table |
 * | `FAILED`    | **one**, linking to the per-item reasons |
 * | `EXPIRED`   | none — we stopped asking; we do not know anything is wrong |
 *
 * Same discipline as `danger` in §05.4: reserve the interrupting affordance for
 * things that genuinely need attention, or it stops meaning anything. With bulk
 * operations and SMS sends, several jobs can land at once — a toast per
 * completion would be noise exactly when the operator is busiest.
 *
 * **It lives here, once, and not in the screens.** Nine screens each raising
 * their own would be nine chances to toast a `SUCCEEDED`, and the operator would
 * see duplicates whenever two screens were mounted. `JobToasts` is rendered
 * once, by the drawer host.
 */

import type { ClientJob, JobStatus } from '@rd/shared';
import { useEffect, useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { useToast, type ToastTone } from '../../components/Toast.js';
import { useJobDrawer } from '../../lib/JobDrawerContext.js';
import { useJob } from '../../lib/useJob.js';

/**
 * The statuses that interrupt, and how. Hand-written from §05.8's table — not
 * derived from `describeJob`, whose tones answer a different question (how to
 * render) and would silently drag `EXPIRED` in with them if it ever changed.
 */
const TOASTED: Partial<Record<JobStatus, { tone: ToastTone; message: (job: ClientJob) => string }>> =
  {
    PARTIAL: {
      tone: 'warning',
      message: (job) =>
        `${job.counts.succeeded} of ${job.counts.total} succeeded. ${
          job.counts.total - job.counts.succeeded
        } need attention.`,
    },
    FAILED: {
      tone: 'error',
      // §04.5's reconciled wording, verbatim. The first draft of this file used
      // §05.8's superseded string — precisely the drift the reconciliation was
      // meant to end, so it is quoted rather than paraphrased.
      message: () => 'This did not run. Nothing was changed.',
    },
  };

export function shouldToast(status: JobStatus): boolean {
  return Object.prototype.hasOwnProperty.call(TOASTED, status);
}

/**
 * Watches one tracked job. Rendered per ref rather than looping inside one
 * component, because `useJob` is a hook and cannot be called in a loop.
 */
function JobToastWatcher({ jobRef }: { jobRef: string }): null {
  const { job } = useJob(jobRef);
  const toast = useToast();
  const navigate = useNavigate();

  // The status we have already reacted to. Comparing against this is what makes
  // it fire on the *transition* rather than on every poll — the drawer polls
  // every 5 s, so without it a PARTIAL job would toast twelve times a minute.
  const announced = useRef<JobStatus | null>(null);

  useEffect(() => {
    if (!job) return;
    if (announced.current === job.status) return;

    const previous = announced.current;
    announced.current = job.status;

    // Never announce the first status we ever see for a job that was already
    // terminal when we started watching it — that is a page reload, not a
    // completion the operator just caused.
    if (previous === null && job.terminal) return;

    const rule = TOASTED[job.status];
    if (!rule) return;

    toast.push(rule.tone, rule.message(job), {
      label: 'View job',
      onClick: () => void navigate(`/jobs/${job.ref}`),
    });
  }, [job, toast, navigate]);

  return null;
}

/** Rendered once, by the drawer host. */
export function JobToasts(): ReactElement {
  const { tracked } = useJobDrawer();
  return (
    <>
      {tracked.map((ref) => (
        <JobToastWatcher key={ref} jobRef={ref} />
      ))}
    </>
  );
}
